<script lang="ts">
  import type { MainMenu, SubMenu } from "$types";
  import {
    menu,
    activeSection,
    colors,
    activeColor,
    logging_in_spinner,
  } from "$lib/data";
  import { page } from "$app/stores";
  import { backOut } from "svelte/easing";
  import { fade, fly } from "svelte/transition";
  import PopOut from "./icons/PopOut.svelte";
  import Squiggle from "./Squiggle.svelte";
  import Avatar from "$components/profile/Avatar.svelte";
  import Notifications from "$components/notifications/Notifications.svelte";
  import Spinner from "$components/forms/Spinner.svelte";
  import { dev } from "$app/environment";
  import NotificationsList from "$components/notifications/NotificationsList.svelte";
  import UserMenu from "./UserMenu.svelte";
  import { LockSimple } from "phosphor-svelte";
  import { afterNavigate } from "$app/navigation";

  let sub: number;
  let window: number;
  let offset: number = 0;
  let subWidth: number = 300;
  let subHeight: number[] = [];
  let nibAdjuster: number = 0;
  const breakPoint: number = 1500; // the point at which the menu aligns for smaller screens
  let is_sub_open: boolean = false;

  // set the position of the sub menu
  const setMenu = (index: number, elementWidth: number) => {
    // use Sub to display the sub menu (index)
    sub = index;

    let tempOffset = 0;
    // run a quick for loop to add all the widths together based on index
    // temp offset will = the distance to left of the active span
    let x = 0;
    while (x < index) {
      tempOffset += menu[x].width ?? 0;
      x++;
    }

    offset =
      index == menu.length - 1 && window < breakPoint
        ? tempOffset + elementWidth / 2 - subWidth / 2 - 90
        : tempOffset + elementWidth / 2 - subWidth / 2;
    nibAdjuster = index == menu.length - 1 && window < breakPoint ? 90 : 0;
  };

  let showMenu: boolean;

  // after a menu item is selected, wait 500ms and then fade the dropdown out
  const menuItemClicked = (index: number) => {
    $activeSection = menu[index].section;
    $activeColor = index;
  };

  // handle some logic for touch devices that have a desktop display width
  let lastSetFromIndex: number;
  const touchMenu = (item: MainMenu, index: number) => {
    setMenu(index, item.width ?? 0);
    showMenu = lastSetFromIndex !== index ? true : false; // this turns the main link into a toggle
    lastSetFromIndex = index;
  };

  // take care of all hover-states on mouse out
  const hideMenu = () => {
    showMenu = false;

    // loop through the entire menu and set all hover-states to false
    menu.forEach((item: MainMenu) => {
      item.sub.forEach((sub: SubMenu) => {
        sub.hover = false;
      });
    });
  };

  // our stores are re-rendering a bunch. We should separate them out
  let logged_in = false;
  $: if ($page.data.user?.id) {
    logged_in = true;
  } else {
    logged_in = false;
  }

  // the sub menu is jumping back n fourth on certain pixels
  // let's solve that with a debounce function that can work
  // either on mouse enter or mouse leave
  // ### there is also a debounce in the on:mouseenter inside of the menu

  let prevent_bounce: boolean = false; // document scoped variable
  let timeoutId: ReturnType<typeof setTimeout>;
  let innerTimeoutId: ReturnType<typeof setTimeout>;
  const debouncer: number = 251;
  const sub_debounce = () => {
    prevent_bounce = true;
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      prevent_bounce = false;
    }, debouncer);
  };

  // trying to fix weird hover states with the member highlighted sign up button
  let is_support_button_hover: boolean = false;

  // user menu
  let show_user_menu: boolean = false;

  // user menu
  let show_notifications_menu: boolean = false;
  let show_notifications_button: boolean = true;

  // a function to unload and reload the notifications menu for dev work
  const reload_notifications = () => {
    show_notifications_button = false;
    setTimeout(() => {
      show_notifications_button = true;
    }, 1000);
  };

  afterNavigate(() => {
    show_notifications_menu = false;
    show_user_menu = false;
  });
</script>

<svelte:window bind:innerWidth={window} />
<div id="navigation">
  <nav
    aria-hidden="true"
    on:mouseenter={() => (showMenu = true)}
    on:mouseleave={hideMenu}
    on:mouseleave={() => (prevent_bounce = false)}
  >
    {#each menu as item, index}
      <a
        rel={item.external ? "external" : ""}
        style="
                    --primary: {colors[index].color};
                    --secondary: {colors[index].highlight};
                "
        bind:clientWidth={item.width}
        data-sveltekit-preload-data="hover"
        on:mouseenter={() => setMenu(index, item.width ?? 0)}
        on:mouseenter={() => (show_user_menu = false)}
        on:mouseenter={() => (show_notifications_menu = false)}
        href={menu[index].sub[0].loggedInHref && logged_in
          ? menu[index].sub[0].loggedInHref
          : menu[index].sub[0].href}
        class:active={$activeSection === item.section}
        on:touchstart|preventDefault={() => touchMenu(item, index)}
        on:click={() => ($activeSection = item.section)}
        on:click={() => ($activeColor = index)}
        tabindex="-1"
      >
        {#if item.loggedInText && logged_in}
          {item.loggedInText}
        {:else}
          {item.text}
        {/if}
        {#if $activeSection === item.section}
          <Squiggle section={index} />
        {/if}
      </a>
    {/each}
    {#if showMenu}
      <div
        in:fade={{ duration: 200 }}
        out:fade={{ delay: is_sub_open ? 125 : 0, duration: 200 }}
        id="subMenu"
        bind:offsetWidth={subWidth}
        style="
                    left: {offset}px;
                    width: {subWidth}px;
                    height: calc( {subHeight[sub]}px + 2rem);
                    top: calc(100% - 4px);
                "
      >
        <svg
          style="
                        left: calc( 50% - {10 - nibAdjuster}px )
                    "
          id="nib"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          x="0px"
          y="0px"
          width="20px"
          height="15px"
          viewBox="0 0 20 14.308"
          enable-background="new 0 0 20 14.308"
          xml:space="preserve"
        >
          <path
            fill="#FFFFFF"
            d="M20,15.712H0c0,0,8.394-13.702,8.999-14.308s1.37-0.631,2.002,0S20,15.712,20,15.712z"
          />
        </svg>
        {#each menu as item, index}
          {@const subMenu =
            (menu[index].loggedInSub ?? []).length > 0 && logged_in
              ? menu[index].loggedInSub
              : menu[index].sub}
          <ul
            class={item.section}
            class:active={sub == index}
            bind:offsetHeight={subHeight[index]}
            style="
                                --primary: {colors[index].color};
                                --secondary: {colors[index].highlight};
                            "
          >
            {#each subMenu as item, sub_index}
              <li
                class:active={$page.url.pathname === item.href &&
                  (item.sub ?? []).length === 0}
                class:hover={((item.sub ?? []).length > 0 && item.hover) ||
                  ($page.url.pathname === item.href &&
                    (item.sub ?? []).length > 0)}
                on:mouseenter={() => {
                  is_support_button_hover = false;
                  if ((item.sub ?? []).length === 0) return; // escape hatch
                  if (prevent_bounce) {
                    // inline debounce function
                    clearTimeout(innerTimeoutId);
                    innerTimeoutId = setTimeout(() => {
                      item.hover = true;
                      menu[index].sub.forEach((each_item, each_index) => {
                        if (each_index === sub_index) return;
                        each_item.hover = false;
                        if ((each_item.sub ?? []).length > 0) {
                          each_item.sub?.forEach(
                            (each_sub) => (each_sub.hover = false),
                          );
                        }
                      });
                    }, debouncer);
                  } else {
                    item.hover = true; // just do it
                    menu[index].sub.forEach((each_item, each_index) => {
                      if (each_index === sub_index) return;
                      each_item.hover = false;
                      if ((each_item.sub ?? []).length > 0) {
                        each_item.sub?.forEach(
                          (each_sub) => (each_sub.hover = false),
                        );
                      }
                    });
                  }
                  sub_debounce();
                }}
                on:mouseleave={() => (item.hover = false)}
              >
                <a
                  href={item.loggedInHref && logged_in
                    ? item.loggedInHref
                    : item.href}
                  on:click={() => menuItemClicked(index)}
                  tabindex="-1"
                  data-sveltekit-preload-data="hover"
                  rel={item.external ? "external" : ""}
                >
                  <div class="icon">
                    {#if (item.sub ?? []).length === 0 || item.override_arrows}
                      <svelte:component
                        this={item.loggedInComponent && logged_in
                          ? item.loggedInComponent
                          : item.component}
                        size={22}
                        color={$page.url.pathname === item.href
                          ? "var(--secondary)"
                          : item.hover
                            ? "var(--primary)"
                            : "#999"}
                      />
                    {:else}
                      <PopOut
                        rotate={window < breakPoint && index > 1}
                        size={26}
                        color={$page.url.pathname === item.href
                          ? "var(--secondary)"
                          : item.hover
                            ? "var(--primary)"
                            : "#999"}
                      />
                    {/if}
                  </div>
                  <div class="stack">
                    <div class="title">
                      {#if item.loggedInText && logged_in}
                        {item.loggedInText}
                      {:else}
                        {item.text}
                      {/if}
                    </div>
                    <div class="subTitle">
                      {#if item.loggedInTitle && logged_in}
                        {item.loggedInTitle}
                      {:else}
                        {item.title}
                      {/if}
                    </div>
                  </div>
                </a>
                {#if (item.sub ?? []).length > 0 && item.hover}
                  <svg
                    on:mouseenter|stopPropagation
                    in:fly={{
                      delay: 100,
                      duration: 700,
                      y: 20,
                    }}
                    out:fade={{ duration: 100 }}
                    class:align_right={(window < breakPoint && index > 1) ||
                      (window < 1775 && index === 3)}
                    class="sub_nib"
                    xmlns="http://www.w3.org/2000/svg"
                    xmlns:xlink="http://www.w3.org/1999/xlink"
                    x="0px"
                    y="0px"
                    width="20px"
                    height="15px"
                    viewBox="0 0 20 14.308"
                    enable-background="new 0 0 20 14.308"
                    xml:space="preserve"
                    aria-hidden
                  >
                    <path
                      fill="#FFFFFF"
                      d="M20,15.712H0c0,0,8.394-13.702,8.999-14.308s1.37-0.631,2.002,0S20,15.712,20,15.712z"
                    />
                  </svg>
                  <ul
                    transition:fly={{
                      duration: 350,
                      x: -30,
                      easing: backOut,
                    }}
                    class:align_right={(window < breakPoint && index > 1) ||
                      (window < 1775 && index === 3)}
                    class:no_gap={sub_index === 0 && !logged_in && index === 3}
                  >
                    {#each item.sub ?? [] as sub}
                      <li class:active={$page.url.pathname === sub.href}>
                        <a
                          class:auth_required={sub.auth_required && !logged_in}
                          class:super={sub.superhighlight && !logged_in}
                          data-sveltekit-preload-data="hover"
                          class:highlighted={is_support_button_hover}
                          href={sub.loggedInHref && logged_in
                            ? sub.loggedInHref
                            : sub.href}
                          on:mouseenter={() => {
                            sub.hover = true;
                            is_sub_open = true;
                            if (sub.superhighlight)
                              is_support_button_hover = true;
                          }}
                          on:mouseleave={() => (sub.hover = false)}
                          on:mouseleave={() => (is_sub_open = false)}
                          on:mouseleave={() => {
                            sub.hover = false;
                            is_sub_open = false;
                            if (sub.superhighlight)
                              is_support_button_hover = false;
                          }}
                          tabindex="-1"
                          rel={sub.external ? "external" : ""}
                        >
                          <div class="icon">
                            <!-- svelte-ignore missing-declaration -->
                            <svelte:component
                              this={sub.loggedInComponent && logged_in
                                ? sub.loggedInComponent
                                : sub.component}
                              size="20"
                              color="{sub.superhighlight && !logged_in
                                ? 'white'
                                : $page.url.pathname === sub.href
                                  ? 'var(--secondary)'
                                  : sub.hover
                                    ? 'var(--primary)'
                                    : '#999'}
                                                        "
                            />
                          </div>
                          <div class="stack">
                            <div class="title">
                              {#if sub.loggedInText && logged_in}
                                {sub.loggedInText}
                              {:else}
                                {sub.text}
                              {/if}
                            </div>
                            <div class="subTitle">
                              {#if sub.loggedInTitle && logged_in}
                                {sub.loggedInTitle}
                              {:else}
                                {sub.title}
                              {/if}
                            </div>
                          </div>
                        </a>
                      </li>
                    {/each}
                  </ul>
                {/if}
              </li>
            {/each}
          </ul>
        {/each}
      </div>
    {/if}
  </nav>
  {#if $page.data.user?.id}
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="user_cont">
      {#if show_notifications_button && $page.data.user?.support_level > 0}
        <button
          class="avatar"
          on:click={() => {
            show_notifications_menu = !show_notifications_menu;
            show_user_menu = false;
          }}
        >
          <Notifications size={45} active={show_notifications_menu} />
        </button>
        {#if show_notifications_menu}
          <nav
            in:fly={{ delay: 200, duration: 100, y: 50 }}
            out:fly={{ duration: 100, y: 50 }}
            class="notifications_menu"
            style="
                --primary: {colors[$activeColor].color};
                --secondary: {colors[$activeColor].highlight};
                "
          >
            <NotificationsList />
            <div
              class="nib"
              in:fly={{ delay: 250, duration: 200, y: 10 }}
              out:fade={{ duration: 100 }}
            >
              <svg
                on:mouseenter|stopPropagation
                xmlns="http://www.w3.org/2000/svg"
                xmlns:xlink="http://www.w3.org/1999/xlink"
                x="0px"
                y="0px"
                viewBox="0 0 20 14.308"
                enable-background="new 0 0 20 14.308"
                xml:space="preserve"
                aria-hidden
              >
                <path
                  fill="#FFFFFF"
                  d="M20,15.712H0c0,0,8.394-13.702,8.999-14.308s1.37-0.631,2.002,0S20,15.712,20,15.712z"
                />
              </svg>
            </div>
          </nav>
        {/if}
      {/if}
      <button
        class="avatar"
        on:click={() => {
          show_user_menu = !show_user_menu;
          show_notifications_menu = false;
        }}
      >
        <Avatar user={$page.data.user} hoverable active={show_user_menu} />
      </button>
      {#if show_user_menu}
        <nav
          in:fly={{ delay: 200, duration: 100, y: 50 }}
          out:fly={{ duration: 100, y: 50 }}
          class="user_menu"
          style="
                --primary: {colors[$activeColor].color};
                --secondary: {colors[$activeColor].highlight};
                "
        >
          {#if dev}
            <button on:click={reload_notifications}>
              cycle notifications
            </button>
          {/if}
          <UserMenu />
          <div
            class="nib"
            in:fly={{ delay: 250, duration: 200, y: 10 }}
            out:fade={{ duration: 100 }}
          >
            <svg
              on:mouseenter|stopPropagation
              xmlns="http://www.w3.org/2000/svg"
              xmlns:xlink="http://www.w3.org/1999/xlink"
              x="0px"
              y="0px"
              viewBox="0 0 20 14.308"
              enable-background="new 0 0 20 14.308"
              xml:space="preserve"
              aria-hidden
            >
              <path
                fill="#FFFFFF"
                d="M20,15.712H0c0,0,8.394-13.702,8.999-14.308s1.37-0.631,2.002,0S20,15.712,20,15.712z"
              />
            </svg>
          </div>
        </nav>
      {/if}
    </div>
  {:else}
    <div class="login_button">
      <a
        href="/login"
        class:disabled={$page.url.pathname === "/login"}
        on:click={() => ($logging_in_spinner = true)}
      >
        <LockSimple size={18} />
        Login
        {#if $logging_in_spinner}
          <div class="login_spinner" in:fade={{ duration: 200 }}>
            <div class="spin">
              <Spinner tiny size_to_container />
            </div>
          </div>
        {/if}
      </a>
    </div>
  {/if}
</div>

<style>
  .notification_count {
    position: absolute;
    top: -0.35rem;
    right: -0.35rem;
    background: var(--primary);
    color: white;
    border-radius: 0.9rem;
    padding: 0.1rem 0.4rem;
    font-size: 0.75rem;
    font-weight: bold;
    z-index: 10;
    display: grid;
    place-content: center;
    border: 2px solid white;
    box-shadow: var(--shadow);
    transition: all 200ms ease;
  }
  .notification_count.maxed {
    right: -0.7rem;
  }

  .login_button {
    margin-left: 1.5rem;
    text-decoration: none;
    font-size: 1rem;
    font-family: "Bitter";
  }
  .login_button a {
    white-space: nowrap;
    color: white;
    display: inline-flex;
    border-radius: 0.7rem;
    padding: 1rem 1.2rem;
    text-decoration: none;
    position: relative;
    overflow: hidden;
    box-shadow: var(--shadow);
    background: var(--secondary);
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .login_button a:hover {
    background: var(--primary);
  }
  .login_button a.disabled {
    background: var(--offDark);
    pointer-events: none;
  }
  .login_button a .login_spinner {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: var(--secondary);
    display: grid;
    place-content: center;
  }
  .login_button a .login_spinner .spin {
    width: 1.2rem;
    height: 1.2rem;
  }

  #navigation {
    display: flex;
    align-items: center;
    position: relative;
  }
  #navigation .avatar {
    width: 45px;
    height: 45px;
    border: 0;
    margin: 0;
    padding: 0;
    background: transparent;
    cursor: pointer;
    position: relative;
  }
  #navigation .user_cont {
    padding-top: 5px;
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding-left: 1rem;
  }

  .user_menu,
  .notifications_menu {
    position: absolute;
    right: 3px;
    top: calc(100% - 0.2rem);
    display: flex;
    flex-flow: column;
    background: white;
    padding: 1rem;
    box-shadow: var(--shadow);
    border-radius: 1rem 0.5rem 1rem 1rem;
    align-items: flex-start;
    min-width: 220px;
    z-index: 14;
  }
  .user_menu .nib,
  .notifications_menu .nib {
    position: absolute;
    top: -16px;
    right: 20px;
    width: 24px;
    height: 16px;
  }
  .user_menu .nib svg,
  .notifications_menu .nib svg {
    position: absolute;
    width: 1.5rem;
    height: 1rem;
    bottom: 0;
    left: 0.8rem;
  }
  .user_menu a,
  .notifications_menu a {
    font-family: "Open Sans";
    padding: 0.5rem;
    font-size: 0.8rem;
    display: flex;
    gap: 0.6rem;
    align-items: center;
    width: 100%;
  }
  .user_menu a:last-of-type,
  .notifications_menu a:last-of-type {
    background: var(--primary);
    color: white;
    flex-grow: 1;
    width: 100%;
    justify-content: flex-start;
    border-radius: 0.25rem;
    margin-top: 0.5rem;
  }
  .user_menu a:last-of-type:hover,
  .notifications_menu a:last-of-type:hover {
    background: var(--secondary);
  }

  .notifications_menu {
    right: calc(41px + 1rem);
  }

  nav {
    display: flex;
    align-items: center;
    font-family: Bitter;
    font-weight: bold;
    font-size: 1.1rem;
    position: relative;
    margin-top: 4px;
  }
  nav #subMenu {
    position: absolute;
    background: white;
    transition: all 200ms ease;
    box-shadow:
      0 5px 60px 0 hsla(234, 100%, 75.9%, 0.12),
      0 14px 90px 0 hsla(234, 100%, 75.9%, 0.12);
    border-radius: 1.5rem;
    padding: 1rem;
    z-index: 10;
  }
  nav #subMenu ul {
    list-style: none;
    margin: 0;
    padding: 0;
    position: absolute;
    opacity: 0;
    transition: all 200ms ease;
    background: white;
    z-index: 1;
    width: calc(100% - 2rem);
  }
  nav #subMenu ul.active {
    opacity: 1;
    transition: all 200ms ease;
    z-index: 2;
  }
  nav #subMenu ul li {
    position: relative;
  }
  nav #subMenu ul li:not(:last-child) {
    margin-top: 1px;
  }
  nav #subMenu ul li.active {
    background: #f1f5f7;
    border-radius: 0.5rem;
  }
  nav #subMenu ul li.active > a {
    color: var(--primary);
  }
  nav #subMenu ul li.hover {
    background: hsl(195, 0%, 98%);
    border-radius: 0.5rem;
  }
  nav #subMenu ul li.hover > a {
    color: var(--primary);
  }
  nav #subMenu ul li > a {
    text-decoration: none;
    color: var(--title);
    display: flex;
    flex-flow: row;
    padding: 0.75rem;
    font-family: "Open Sans";
  }
  nav #subMenu ul li > a:hover {
    color: var(--secondary);
  }
  nav #subMenu ul li > a .icon {
    width: 2rem;
    min-width: 2rem;
    max-width: 2rem;
  }
  nav #subMenu ul li > a .icon svg {
    width: 1rem;
    height: 1rem;
  }
  nav #subMenu ul li > a .stack {
    padding-left: 1rem;
    display: flex;
    flex-flow: column;
    gap: 0.2rem;
  }
  nav #subMenu ul li > a .stack .title {
    padding-top: 0.2rem;
    font-size: 0.9rem;
  }
  nav #subMenu ul li > a .stack .subTitle {
    font-size: 0.8rem;
    line-height: 1.3;
    font-weight: normal;
    font-family: "Open Sans", sans-serif;
    color: #999;
    padding-top: 0.2rem;
  }
  nav #subMenu ul li ul {
    position: absolute;
    background: white;
    left: calc(100% - 2px);
    top: 0;
    z-index: 10;
    opacity: 1;
    border-radius: 1rem;
    font-size: 80%;
    box-shadow: var(--shadow);
    padding: 1rem;
    width: 280px;
  }
  nav #subMenu ul li ul.align_right {
    left: auto;
    right: calc(100% - 2px);
  }
  nav #subMenu ul li ul li a {
    color: var(--title);
  }
  nav #subMenu ul li ul li a .icon {
    width: 15px;
  }
  nav #subMenu ul li ul li .auth_required .title {
    color: #555 !important;
    text-shadow: 2px 2px 0 white;
  }
  nav #subMenu ul li ul li .auth_required .subTitle {
    color: #666 !important;
    text-shadow: -1px -1px 0 white;
  }
  nav #subMenu ul li ul li .super {
    background: var(--secondary);
    border-radius: 0.75rem 0.75rem 0 0;
    position: relative;
    z-index: 14 !important;
    overflow: hidden;
  }
  nav #subMenu ul li ul li .super:hover {
    background: var(--primary);
  }
  nav #subMenu ul li ul li .super::after {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    transform: translateX(-100%);
    background-image: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0,
      rgba(255, 255, 255, 0.05) 20%,
      rgba(255, 255, 255, 0.3) 50%,
      rgba(255, 255, 255, 0)
    );
    animation: 1s ease-out 1s shimmer 1;
    content: "";
  }
  nav #subMenu ul li ul li .super .title {
    border-bottom: rgba(255, 255, 255, 0.4);
    color: white;
  }
  nav #subMenu ul li ul li .super .subTitle {
    font-weight: bold;
    color: white;
  }
  nav #subMenu ul li .sub_nib {
    pointer-events: none;
    position: absolute;
    left: calc(100% - 1rem);
    top: 1rem;
    transform: rotate(-90deg);
    z-index: 11;
    animation: z_switch 150ms once;
  }
  nav #subMenu ul li .sub_nib.align_right {
    left: auto;
    right: calc(100% - 1rem);
    transform: rotate(90deg);
  }
  nav #subMenu #nib {
    position: absolute;
    top: -15px;
    transition: all 200ms ease;
  }
  nav a {
    padding: 1.2rem;
    color: var(--title);
    position: relative;
    cursor: pointer;
    text-decoration: none;
  }
  nav a:hover {
    color: var(--primary);
  }
  nav a.active {
    color: var(--primary);
  }

  .no_gap {
    background-color: #ffffff;
    background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23e8ebf3' fill-opacity='0.4' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E") !important;
  }
  .no_gap li {
    margin-top: 0 !important;
  }

  @keyframes shimmer {
    100% {
      transform: translateX(100%);
    }
  }
</style>
